import { Injectable, ErrorHandler } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from './toast.service';
import { LoggerService } from './logger.service';
import { Router } from '@angular/router';
import { isCameraPhotoOptionsError } from '../shared/utils/error.util';

/**
 * Centralized global error handling service for the application. Handles, logs, and displays user-friendly messages for HTTP, runtime, validation, network, and other uncaught errors, and can perform routing actions (e.g., redirect to login) when necessary.
 * @component
 * @selector app-global-error-handler
 * @example
 *   <app-global-error-handler></app-global-error-handler>
 * @input none NONE - This injectable service does not expose Angular @Input properties; it is configured and used via dependency injection (LoggerService, ToastService, Router).
 * @output none NONE - This service does not emit Angular @Output events; it reports errors via LoggerService, shows toasts via ToastService, and navigates via Router.
 *
 * Purpose:
 *   - Provide a single place to handle all uncaught exceptions and HTTP errors across the app.
 *   - Suppress noisy or expected errors (auth flows, change-detection errors, early post-navigation errors) to avoid spamming users during user-type switching or login flows.
 *   - Convert technical errors into user-friendly messages where appropriate and ensure important errors are logged with context.
 *
 * Injected dependencies:
 *   - LoggerService: used for structured error logging with contextual metadata.
 *   - ToastService: used to show user-facing toast notifications (severity, summary, detail, life).
 *   - Router: used to determine current route context and perform navigations (e.g., redirect to /login on 401).
 *
 * Key methods:
 *   - handleError(error: any): Entry point implementing Angular's ErrorHandler. Logs the error and delegates to specialized handlers based on the error type (HttpErrorResponse, TypeError, ReferenceError, or generic).
 *   - handleHttpError(error: HttpErrorResponse): Handles HTTP-specific logic including auth-endpoint suppression, login-page suppression, detailed logging, and per-status handling (401, 403, 404, 500, default user-friendly message).
 *   - handleTypeError(error: TypeError): Logs TypeError details for debugging. (Optionally show user message.)
 *   - handleReferenceError(error: ReferenceError): Logs and shows a refresh suggestion for reference errors.
 *   - handleGenericError(error: any): Handles non-HTTP and non-technical-specific errors. Detects auth-related messages, change-detection errors (ExpressionChangedAfterItHasBeenCheckedError / NG0100), and suppresses notifications for errors occurring shortly after navigation (e.g., < 3s on dashboard). Logs detailed context and shows a generic user-friendly message when appropriate.
 *   - handle401Error(): Handles 401 Unauthorized by redirecting to the login route (and could show a session-expired message).
 *   - handle403Error(): Shows a permission-denied message to the user.
 *   - handle404Error(error: HttpErrorResponse): Shows a not-found message for missing resources.
 *   - handle500Error(): Placeholder for server error handling; can show a server error message or log additional context.
 *   - getHttpErrorMessage(error: HttpErrorResponse): Extracts the most relevant message from nested HTTP error payloads or falls back to a generic message.
 *   - showUserFriendlyMessage(message: string): Wrapper to send an error toast via ToastService with consistent formatting and lifetime.
 *   - handleComponentError(error: any, componentName: string, context?: any): Utility for components to report and display errors in a consistent way; logs and shows either an extracted HTTP message or a generic message.
 *   - handleValidationError(errors: any, formName: string): Logs validation issues and shows a warning toast that prompts the user to fix form errors.
 *   - handleNetworkError(): Logs a network error and shows a toast instructing the user to check their internet connection.
 *
 * Important behavior & lifecycle notes:
 *   - Provided in root (providedIn: 'root') so it is a singleton across the app.
 *   - Implements Angular's ErrorHandler interface; when registered as the app-wide error handler, Angular will call handleError for uncaught exceptions.
 *   - Suppresses toasts for certain contexts to avoid user confusion: requests to auth endpoints, when on the login page, Angular change-detection errors, and errors that occur immediately after navigation (configurable short window).
 *   - Contains extensive console and structured logger output to aid developers in reproducing and debugging issues (includes route, timestamp, status, and error payload).
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  constructor(
    private logger: LoggerService,
    private messageService: ToastService,
    private router: Router
  ) {}

  /**
   * Logs error details (including type, message, route and timestamp) and delegates handling to specialized handlers based on the error's type.
   * @example
   * andleError(new Error('Sample error'))
   * undefined
   * @param {{any}} {{error}} - The error object to be handled.
   * @returns {{void}} No return value; performs logging and delegates handling.
   **/
  handleError(error: any): void {
    if (isCameraPhotoOptionsError(error)) {
      this.logger.debug(
        'Ignoring mobile camera photo-options error from barcode scanner',
        {
          message: error?.message,
          name: error?.name,
          currentRoute: this.router.url,
        },
        'GlobalErrorHandler'
      );
      return;
    }

    this.logger.debug(
      'GlobalErrorHandler.handleError called',
      {
        errorType: error?.constructor?.name,
        errorMessage: error?.message,
        currentRoute: this.router.url,
        timestamp: new Date().toISOString(),
        error: error,
      },
      'GlobalErrorHandler'
    );

    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else if (error instanceof TypeError) {
      this.handleTypeError(error);
    } else if (error instanceof ReferenceError) {
      this.handleReferenceError(error);
    } else {
      this.handleGenericError(error);
    }
  }

  /**
   * Handle an HTTP error by logging details, suppressing toast for auth/login routes, and delegating to specific error handlers.
   * @example
   * handleHttpError(sampleHttpError)
   * undefined
   * @param {{HttpErrorResponse}} {{error}} - The HTTP error response to handle.
   * @returns {{void}} Does not return a value.
   **/
  private handleHttpError(error: HttpErrorResponse): void {
    const errorMessage = this.getHttpErrorMessage(error);

    // Check if we're on the login page - don't show errors during login/logout
    const isOnLoginPage = this.router.url.includes('/login');

    // Check if this is an auth-related endpoint - don't show errors for these
    const url = error.url?.toLowerCase() || '';
    const isAuthEndpoint =
      url.includes('/user/auth/login') ||
      url.includes('/user/login') ||
      url.includes('/user/auth/logout') ||
      url.includes('/user/logout') ||
      url.includes('/user/auth/session') ||
      url.includes('/user/validate-token') ||
      url.includes('/user/auth/password/forgot') ||
      url.includes('/user/forgot-password') ||
      url.includes('/user/auth/password/reset') ||
      url.includes('/user/reset-password');

    this.logger.debug(
      'GlobalErrorHandler - HTTP Error Details',
      {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        currentRoute: this.router.url,
        isOnLoginPage,
        isAuthEndpoint,
        errorMessage,
        errorDetails: error.error,
      },
      'GlobalErrorHandler'
    );

    this.logger.error(
      'HTTP Error occurred',
      {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        currentRoute: this.router.url,
        isOnLoginPage,
        isAuthEndpoint,
        message: error.message,
        error: error.error,
      },
      'GlobalErrorHandler'
    );

    // Don't show error toasts for auth endpoints or when on login page
    // This prevents error messages when switching between user types
    if (isAuthEndpoint || isOnLoginPage) {
      this.logger.debug(
        'Suppressing error toast - Auth endpoint or on login page',
        { url: error.url },
        'GlobalErrorHandler'
      );
      return;
    }

    // Handle specific HTTP error codes
    switch (error.status) {
      case 401:
        this.handle401Error();
        break;
      case 403:
        this.handle403Error();
        break;
      case 404:
        this.handle404Error(error);
        break;
      case 500:
        this.handle500Error();
        break;
      default:
        this.showUserFriendlyMessage(errorMessage);
        break;
    }
  }

  private handleTypeError(error: TypeError): void {
    this.logger.error(
      'Type Error occurred',
      {
        message: error.message,
        stack: error.stack,
      },
      'GlobalErrorHandler'
    );
  }

  private handleReferenceError(error: ReferenceError): void {
    this.logger.error(
      'Reference Error occurred',
      {
        message: error.message,
        stack: error.stack,
      },
      'GlobalErrorHandler'
    );

    this.showUserFriendlyMessage('A technical error occurred. Please refresh the page.');
  }

  /**
   * Handle generic runtime errors, log detailed diagnostics, and conditionally show a user-friendly toast.
   * @example
   * handleGenericError(new Error('Sample error'))
   * undefined
   * @param {any} error - The thrown error to process; can be an Error instance or any value.
   * @returns {void} No return value.
   **/
  private handleGenericError(error: any): void {
    // Check if we're on the login page - don't show errors during login/logout
    const isOnLoginPage = this.router.url.includes('/login');

    // Check if this is an auth-related error - don't show errors for these
    const errorMessage = error?.message || '';
    // Guard against circular-reference objects (e.g. some Stripe/Pusher error shapes)
    // that would cause JSON.stringify to throw inside the error handler itself.
    let errorString = '';
    try {
      errorString = JSON.stringify(error).toLowerCase();
    } catch {
      errorString = String(error).toLowerCase();
    }
    const isAuthError =
      errorMessage.toLowerCase().includes('token') ||
      errorMessage.toLowerCase().includes('login') ||
      errorMessage.toLowerCase().includes('logout') ||
      errorMessage.toLowerCase().includes('authentication') ||
      errorMessage.toLowerCase().includes('session') ||
      errorMessage.toLowerCase().includes('unauthorized') ||
      errorString.includes('token') ||
      errorString.includes('login') ||
      errorString.includes('logout');

    // Suppress Angular change detection errors (ExpressionChangedAfterItHasBeenCheckedError)
    // These are development/debugging errors that occur during state transitions
    // and shouldn't be shown to users, especially when switching user types
    const isChangeDetectionError =
      errorMessage.includes('ExpressionChangedAfterItHasBeenCheckedError') ||
      errorMessage.includes('NG0100') ||
      errorMessage.includes('Expression has changed after it was checked') ||
      error?.name === 'ExpressionChangedAfterItHasBeenCheckedError';

    // Suppress errors that occur on dashboard right after login (within 3 seconds)
    // This catches errors during user type switching
    const isDashboardRoute = this.router.url.includes('/dashboard');
    const timeSincePageLoad = performance.now();
    const isRecentNavigation = isDashboardRoute && timeSincePageLoad < 3000; // 3 seconds

    this.logger.debug(
      'GlobalErrorHandler - Generic Error Details',
      {
        errorType: error?.constructor?.name,
        errorName: error?.name,
        errorMessage,
        currentRoute: this.router.url,
        isOnLoginPage,
        isAuthError,
        isChangeDetectionError,
        isRecentNavigation,
        timeSincePageLoad,
        errorStack: error?.stack,
      },
      'GlobalErrorHandler'
    );

    this.logger.error(
      'Unhandled error occurred',
      {
        errorType: error?.constructor?.name,
        errorName: error?.name,
        message: error?.message || 'Unknown error',
        currentRoute: this.router.url,
        isOnLoginPage,
        isAuthError,
        isChangeDetectionError,
        isRecentNavigation,
        stack: error?.stack,
        error: error,
      },
      'GlobalErrorHandler'
    );

    // Don't show error toasts for:
    // 1. Auth-related errors
    // 2. When on login page
    // 3. Angular change detection errors (common during user type switching)
    // 4. Errors that occur shortly after navigation (likely during user type switching)
    if (isAuthError || isOnLoginPage || isChangeDetectionError || isRecentNavigation) {
      this.logger.debug(
        'Suppressing error toast - Auth error, login page, change detection, or recent navigation',
        {
          isAuthError,
          isOnLoginPage,
          isChangeDetectionError,
          isRecentNavigation,
        },
        'GlobalErrorHandler'
      );
      return;
    }

    this.showUserFriendlyMessage('An unexpected error occurred. Please try again.');
  }

  private handle401Error(): void {
    this.router.navigate(['/login']);
  }

  private handle403Error(): void {
    this.showUserFriendlyMessage('You do not have permission to access this resource.');
  }

  private handle404Error(error: HttpErrorResponse): void {
    this.showUserFriendlyMessage('The requested resource was not found.');
  }

  private handle500Error(): void {}

  private getHttpErrorMessage(error: HttpErrorResponse): string {
    if (error.error?.message) {
      return error.error.message;
    }

    if (error.error?.error?.message) {
      return error.error.error.message;
    }

    return error.message || 'An error occurred';
  }

  private showUserFriendlyMessage(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 5000,
    });
  }

  // Method for components to handle specific errors
  /**
   * Handles an error for a component by logging it and displaying a user-friendly message; treats HttpErrorResponse specially to extract and show detailed messages.
   * @example
   * andleComponentError(new Error('Failure'), 'MyComponent', { action: 'save' })
   * undefined
   * @param {{any}} {{error}} - Error object or HttpErrorResponse to handle.
   * @param {{string}} {{componentName}} - Name of the component where the error occurred.
   * @param {{any}} {{context}} - Optional contextual information to include in logs.
   * @returns {{void}} Return description in one line.
   **/
  handleComponentError(error: any, componentName: string, context?: any): void {
    this.logger.error(
      `Error in ${componentName}`,
      {
        error,
        context,
      },
      componentName
    );

    if (error instanceof HttpErrorResponse) {
      const message = this.getHttpErrorMessage(error);
      this.showUserFriendlyMessage(message);
    } else {
      this.showUserFriendlyMessage('An error occurred. Please try again.');
    }
  }

  // Method for handling validation errors
  handleValidationError(errors: any, formName: string): void {
    this.logger.warn(`Validation errors in ${formName}`, errors, 'ValidationError');

    this.messageService.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please check the form and fix any errors.',
      life: 3000,
    });
  }

  // Method for handling network errors
  handleNetworkError(): void {
    this.logger.error('Network error occurred', {}, 'NetworkError');

    this.messageService.add({
      severity: 'error',
      summary: 'Network Error',
      detail: 'Please check your internet connection and try again.',
      life: 5000,
    });
  }
}
